import { SkyviewAssetPositionProps } from '../../react/components/skyview/tooltip/skyview-asset-position-tooltip';
import { SkyviewCamera } from '../cameras/types';
import { Design } from '../elements/design';
import {
  summarizeHoverMetadata,
  summarizeMetadata,
} from '../elements/design/design-node/utils/meta-data';
import { ElementObj } from '../elements/element-obj';
import { Geometry } from '../elements/geometry';
import { ProjectGeodataManager } from '../project-geodata-manager';
import { Coordinates } from '../utilities/math-helpers';
import { DesignTool } from './design-tool';
import { Tool } from './tool';

type ScreenPoint = {
  x: number;
  y: number;
};

export const buildTooltipProps = (
  asset: ElementObj | undefined,
  camera: SkyviewCamera,
  coordinate: Coordinates,
  screenPoint: ScreenPoint,
  tool?: Tool,
): SkyviewAssetPositionProps => {
  const resolveProps = (): SkyviewAssetPositionProps['properties'] => {
    switch (asset?.assetType) {
      case 'line': {
        const line = asset as Geometry;

        return {
          type: 'line',
          length: line.getDistance()!,
          planeLength: line.getPlaneDistance()!,
          slope: line.getSlope(),
        };
      }

      case 'area': {
        const area = asset as Geometry;

        return {
          type: 'area',
          area: area.getArea()!,
          planeArea: area.getPlaneArea()!,
          circumference: area.getDistance()!,
          length: area.getPlaneDistance()!,
        };
      }
      case 'design':
        if ((tool as DesignTool).multiSelectInProgress) {
          return {
            type: 'design',
            isMultiselect: true,
            metaData: summarizeMetadata(
              (asset as Design).designNode,
              (tool as DesignTool).currentSelection,
            ),
          };
        } else {
          return {
            type: 'design',
            isMultiselect: false,
            metaData: summarizeHoverMetadata(tool as DesignTool),
            curveDistance: (tool as DesignTool).curveDistanceInput,
          };
        }
      default:
        return {
          type: 'coordinates',
        };
    }
  };

  return {
    properties: resolveProps(),
    coordinateSystem: ProjectGeodataManager.instance().getProjectionName(),
    coordinate,
    screenPoint,
  };
};

/**
 * A tool whose tooltip can be shown in a menu or a dialog instead of next to the cursor.
 */
export type MenuTooltipTool = {
  tooltipCallback?: (tooltip?: SkyviewAssetPositionProps) => void;
  /**
   * Set while the tooltip is shown in a menu or a dialog, which then gets it instead of the cursor.
   */
  onMenuTooltipChanged?: (tooltip?: SkyviewAssetPositionProps) => void;
};

/**
 * Shows the tooltip of the tool in a menu or a dialog when it is placed there, otherwise next to
 * the cursor. Without a tooltip, it is hidden from both.
 */
export function showToolTooltip(tool: MenuTooltipTool, tooltip?: SkyviewAssetPositionProps) {
  if (tooltip && !tool.onMenuTooltipChanged) {
    tool.tooltipCallback?.(tooltip);
    return;
  }

  tool.tooltipCallback?.();
  tool.onMenuTooltipChanged?.(tooltip);
}
